import {
  DateUtils,
  DATE_FORMAT_YYYYMMDD,
  DATE_FORMAT_MMDDYYYY
} from "utils/dateutils";
import { IMaterial, UpdateLeadTimeRequestData } from "../type-definitions";
import { mergeSpecSectionNoandName } from "../widgets";
import { toProjectCalendarDateString } from "./project-calendar-date";
import { isCommittedDateComputeStrategy } from "./timeline-variant";
import {
  MaterialDateConfirmationData,
  MaterialDateConfirmationMilestone
} from "./types";

function getMilestoneStatus(
  milestone: Pick<MaterialDateConfirmationMilestone, "actualDate" | "id">,
  activeMilestoneId: string | null
): MaterialDateConfirmationMilestone["status"] {
  if (milestone.actualDate) return "completed";
  if (milestone.id === activeMilestoneId) return "active";
  return "pending";
}

function toViewModelDate(date: string | null | undefined, timezoneId: string) {
  if (!date) return "";
  return toProjectCalendarDateString(date, timezoneId);
}

function formatMaterialCurrentStatus(
  status: string | null | undefined,
  statusDate: string | null | undefined,
  timezoneId: string
) {
  if (!status) return "TBD";
  if (!statusDate) return status;
  return `${status} on ${DateUtils.format(
    toProjectCalendarDateString(statusDate, timezoneId),
    DATE_FORMAT_MMDDYYYY
  )}`;
}

function getNextMilestone(milestones: MaterialDateConfirmationMilestone[]) {
  return milestones.find((milestone) => !milestone.actualDate)?.name || "TBD";
}

function toApiDate(date: string | null | undefined) {
  if (!date) return null;
  return DateUtils.formatDateWithLunchTime(date, DATE_FORMAT_YYYYMMDD);
}

export function mapDateConfirmationDataToApiPayload(
  material: IMaterial,
  data: MaterialDateConfirmationData
): UpdateLeadTimeRequestData {
  const useCommittedDateKey = isCommittedDateComputeStrategy(
    material.dateblock_compute_strategy
  );

  return [
    {
      estimate_invite_feature_id: material.estimate_invite_feature_id,
      feature_instance_id: material.id,
      comment: data.comments || "",
      milestones: (material.milestones || []).map((milestone) => {
        const updatedMilestone = data.milestones.find(
          (item) => item.id === milestone.id
        );
        const dateValue = toApiDate(updatedMilestone?.projectedDate);
        const actualDate = toApiDate(updatedMilestone?.actualDate);

        if (useCommittedDateKey) {
          const milestoneRest = { ...milestone };
          delete milestoneRest.projected_date;
          return {
            ...milestoneRest,
            committed_date: dateValue,
            actual_date: actualDate
          };
        }

        return {
          ...milestone,
          projected_date: dateValue,
          actual_date: actualDate
        };
      })
    }
  ];
}

export function mapOffsetsToApiPayload(
  material: IMaterial,
  values: Record<string, number | string>,
  comment?: string | null
): UpdateLeadTimeRequestData {
  return [
    {
      estimate_invite_feature_id: material.estimate_invite_feature_id,
      feature_instance_id: material.id,
      comment: comment ?? "",
      offsets: material.offsets?.map((offset) => ({
        id: offset.id,
        value: Number(values[offset.id])
      }))
    }
  ];
}

export function mapMaterialToDateConfirmationData(
  material: IMaterial,
  timezoneId: string
): MaterialDateConfirmationData {
  const apiMilestones = [...(material.milestones || [])].sort(
    (a, b) => a.order - b.order
  );
  const activeMilestoneId =
    apiMilestones.find((milestone) => !milestone.actual_date)?.id || null;

  const useCommittedDateKey = isCommittedDateComputeStrategy(
    material.dateblock_compute_strategy
  );

  const milestones = apiMilestones.map((milestone) => {
    const apiDate = useCommittedDateKey
      ? milestone.committed_date
      : milestone.projected_date;

    const mappedMilestone: MaterialDateConfirmationMilestone = {
      id: milestone.id,
      order: milestone.order,
      name: milestone.name,
      projectedDate: apiDate
        ? toProjectCalendarDateString(apiDate, timezoneId)
        : null,
      actualDate: milestone.actual_date
        ? toProjectCalendarDateString(milestone.actual_date, timezoneId)
        : null,
      status: "pending"
    };

    mappedMilestone.status = getMilestoneStatus(
      mappedMilestone,
      activeMilestoneId
    );
    return mappedMilestone;
  });

  const specSection = mergeSpecSectionNoandName(
    material.spec_section_no || null,
    material.spec_section_name || null
  );

  const currentDelay = material.last_milestone_slippage ?? null;

  return {
    materialTitle: material.name,
    requiredOnsiteDate: toViewModelDate(
      material.required_on_site_date,
      timezoneId
    ),
    currentExpectedOnsiteDate: toViewModelDate(
      material.current_expected_on_site,
      timezoneId
    ),
    isExpectedOnsiteAtRisk: (currentDelay ?? 0) > 0,
    currentDelay,
    currentStatus: formatMaterialCurrentStatus(
      material.material_status,
      material.material_status_date,
      timezoneId
    ),
    nextMilestone: getNextMilestone(milestones),
    specSection: specSection || "",
    tag: material.material_tag || "",
    description: material.description || "",
    comments: "",
    milestones
  };
}
